/**
 * Rack Policy - Centralized rules for rack and temporary_rack visibility and editability
 * Used by Production Line tabs, Glass Arrival, and view/edit order pages.
 *
 * Source of truth: existing code behavior and completion-condition logic (code-first).
 */

import type { OrderSummary } from './types';
import { isOrderLeaded } from './leadedDetection';

/** Context where rack is being rendered */
export type RackContext =
  | 'job_details_view'      // Job Details Orders tab (view only)
  | 'orders_edit'           // Orders Edit Panel (always editable when shown)
  | 'order_modal_view'      // View Orders modal (view only)
  | 'production_dashboard'  // Production Line Dashboard
  | 'sealing_allocation'    // Production Line Sealing & Allocation tab
  | 'glass_cutting'         // Production Line Glass Cutting tab
  | 'spacer_cutting'        // Production Line Spacer Bar Cutting tab
  | 'drawing'               // Production Line Drawing tab
  | 'glass_arrival';       // Glass Arrival page

export interface RackPolicyResult {
  /** Whether to show the rack editor (vs placeholder/read-only) */
  showEditor: boolean;
  /** Whether to use rack (false = use temporary_rack) */
  useRack: boolean;
  /** Placeholder text when editor is not shown */
  placeholder: string;
  /** Whether the cell should be read-only (view mode) even when showEditor */
  readOnly: boolean;
}

type RackContextType = 'production_line' | 'orders_edit' | 'job_details' | 'order_modal';

/**
 * Unified policy lookup - routes to production line or default rack policy
 */
export function getRackPolicyForColumn(
  order: OrderSummary,
  columnKey: string,
  rackContext: RackContextType | undefined,
  tableMode: 'view' | 'edit'
): RackPolicyResult {
  if (rackContext === 'production_line' && RACK_PRODUCTION_COLUMNS.includes(columnKey)) {
    return getProductionLineRackPolicy(order, columnKey, tableMode);
  }
  const ctx: RackContext =
    rackContext === 'orders_edit'
      ? 'orders_edit'
      : rackContext === 'order_modal'
        ? 'order_modal_view'
        : 'job_details_view';
  return getRackPolicy(order, ctx, tableMode);
}

const RACK_PRODUCTION_COLUMNS = [
  'rack_dashboard',
  'temporary_rack_dashboard',
  'rack_workflow',
  'rack_glass_cutting',
  'rack_spacer',
  'rack_drawing',
  'temporary_rack_glass_cutting',
  'temporary_rack_drawing',
];

/**
 * Get rack policy for default 'rack' column (used in Job Details, Orders Edit, Order Modal)
 */
export function getRackPolicy(
  order: OrderSummary,
  context: RackContext,
  tableMode: 'view' | 'edit'
): RackPolicyResult {
  // View-only contexts: always read-only display
  if (context === 'job_details_view' || context === 'order_modal_view') {
    return {
      showEditor: true,
      useRack: !isOrderLeaded(order),
      placeholder: '-',
      readOnly: true,
    };
  }

  // Orders Edit: always editable when mode is edit
  if (context === 'orders_edit') {
    if (tableMode === 'view') {
      return { showEditor: true, useRack: !isOrderLeaded(order), placeholder: '-', readOnly: true };
    }
    return { showEditor: true, useRack: !isOrderLeaded(order), placeholder: '-', readOnly: false };
  }

  return { showEditor: false, useRack: true, placeholder: '-', readOnly: true };
}

/**
 * Get rack policy for Production Line tab-specific rack columns
 */
export function getProductionLineRackPolicy(
  order: OrderSummary,
  columnKey: string,
  tableMode: 'view' | 'edit'
): RackPolicyResult {
  const isLeaded = isOrderLeaded(order);
  const isSingle = order.product_type === 'single';

  switch (columnKey) {
    case 'rack_dashboard': {
      // Dashboard: permanent rack only (temporary rack is temporary_rack_dashboard)
      return {
        showEditor: true,
        useRack: true,
        placeholder: '-',
        readOnly: false,
      };
    }

    case 'temporary_rack_dashboard': {
      return {
        showEditor: true,
        useRack: false,
        placeholder: '-',
        readOnly: false,
      };
    }

    case 'rack_workflow': {
      // Sealing & Allocation: only show editable when sealed
      const isSealed = order.metadata?.sealing_workflow?.sealed ?? false;
      if (!isSealed) {
        return {
          showEditor: false,
          useRack: true,
          placeholder: 'Not Sealed',
          readOnly: true,
        };
      }
      return {
        showEditor: true,
        useRack: true,
        placeholder: '-',
        readOnly: false,
      };
    }

    case 'temporary_rack_glass_cutting': {
      const isCut = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
      if (!isLeaded) {
        return { showEditor: false, useRack: false, placeholder: '-', readOnly: true };
      }
      if (!isCut) {
        return { showEditor: false, useRack: false, placeholder: 'Not Cut', readOnly: true };
      }
      return { showEditor: true, useRack: false, placeholder: '-', readOnly: false };
    }

    case 'rack_glass_cutting': {
      const isCut = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
      const isSingleOrMirror =
        order.product_type === 'single' || order.product_type === 'mirror';
      if (isLeaded) {
        return {
          showEditor: false,
          useRack: true,
          placeholder: 'Use Temp Rack',
          readOnly: true,
        };
      }
      if (!isSingleOrMirror) {
        return { showEditor: false, useRack: true, placeholder: '-', readOnly: true };
      }
      if (!isCut) {
        return { showEditor: false, useRack: true, placeholder: 'Not Cut', readOnly: true };
      }
      return { showEditor: true, useRack: true, placeholder: '-', readOnly: false };
    }

    case 'rack_spacer': {
      // Spacer Bar Cutting: No Rack Assignment per docs - column removed from tab
      return {
        showEditor: false,
        useRack: true,
        placeholder: '-',
        readOnly: true,
      };
    }

    case 'temporary_rack_drawing': {
      if (!isLeaded) {
        return { showEditor: false, useRack: false, placeholder: '-', readOnly: true };
      }
      const hasTempRackValue =
        Boolean(order.temporary_rack && String(order.temporary_rack).trim() !== '');
      // Single glaze: if temp rack already set elsewhere, always show it; otherwise gate on Drawing done
      if (isSingle) {
        const drawingDone = order.metadata?.drawing_workflow?.done ?? false;
        if (!hasTempRackValue && !drawingDone) {
          return {
            showEditor: false,
            useRack: false,
            placeholder: 'Not Done',
            readOnly: true,
          };
        }
      }
      return { showEditor: true, useRack: false, placeholder: '-', readOnly: false };
    }

    case 'rack_drawing': {
      if (!isSingle) {
        return { showEditor: false, useRack: true, placeholder: '-', readOnly: true };
      }
      const drawingDone = order.metadata?.drawing_workflow?.done ?? false;
      if (!drawingDone) {
        return {
          showEditor: false,
          useRack: true,
          placeholder: 'Not Done',
          readOnly: true,
        };
      }
      return { showEditor: true, useRack: true, placeholder: '-', readOnly: false };
    }

    default:
      return { showEditor: true, useRack: !isLeaded, placeholder: '-', readOnly: true };
  }
}

/** Result for Glass Arrival rack/temp-rack columns */
export interface GlassArrivalRackPolicy {
  temporaryRack: { showEditor: boolean; placeholder: string };
  rack: { showEditor: boolean; placeholder: string };
}

/**
 * Get rack policy for Glass Arrival page.
 * Temp rack: editable only when leaded + arrived + single.
 * Rack: editable only when NOT leaded + arrived + (single or mirror).
 */
export function getGlassArrivalRackPolicy(order: OrderSummary, isArrived: boolean): GlassArrivalRackPolicy {
  const isLeaded = isOrderLeaded(order);
  const pt = order.product_type;

  const temporaryRack = {
    showEditor: isLeaded && isArrived && pt === 'single',
    placeholder: !isLeaded ? 'Not Leaded' : !isArrived ? 'Not Arrived' : '-',
  };

  const rack = {
    showEditor: !isLeaded && isArrived && (pt === 'single' || pt === 'mirror'),
    placeholder: isLeaded
      ? 'Use Temp Rack'
      : !isArrived
        ? 'Not Arrived'
        : pt === 'double'
          ? 'Double - No Rack'
          : pt === 'triple'
            ? 'Triple - No Rack'
            : !['single', 'mirror', 'double', 'triple'].includes(pt || '')
              ? 'No Rack'
              : '-',
  };

  return { temporaryRack, rack };
}
